<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.legal_entity_name") }}</label>
        <DxSelectBox
          v-model="localData.legal_entity_uuid"
          :data-source="legalEntityList"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onLegalEntityChanged"
          :class="{ 'field-error': $v.localData.legal_entity_uuid.$error }"
          placeholder=""
        />
      </div>
      <div class="form-group col-sm-12 col-lg-2 text-left">
        <label class="control-label" style="margin-bottom: 5px">{{ $t("year") }}</label>
        <DxNumberBox
          v-model="localData.year"
          :min="2000"
          :max="2100"
          :show-spin-buttons="true"
          :class="{ 'field-error': $v.localData.year.$error }"
        />
      </div>
    </div>

    <!-- Movement Types Section -->
    <div class="row" style="margin-top: 16px">
      <div class="col-sm-12">
        <h5 class="section-title">{{ $t("scale.quantity_warnings.movement_types") }}</h5>
      </div>
    </div>

    <!-- Weighing on Scale -->
    <div class="row" style="margin-top: 8px">
      <div class="form-group col-sm-12 col-lg-12 text-left">
        <div class="d-flex align-items-center" style="gap: 12px; margin-bottom: 8px">
          <input type="checkbox" :id="'include_weighing'" v-model="includeWeighing" style="width: 16px; height: 16px; cursor: pointer" />
          <label :for="'include_weighing'" class="control-label mb-0" style="cursor: pointer">
            {{ $t("scale.quantity_warnings.include_weighing_on_scale") }}
          </label>
        </div>
        <div v-if="includeWeighing" style="padding-left: 28px" class="col-sm-12 col-lg-4">
          <DxTagBox
            v-model="localData.weighing_type_ids"
            :data-source="weighingTypesList"
            display-expr="weighing_type"
            value-expr="weighing_type_id"
            :show-selection-controls="true"
            :search-enabled="true"
            :placeholder="$t('select_all')"
            :show-clear-button="true"
          />
        </div>
      </div>
    </div>

    <!-- Stock Movements -->
    <div class="row" style="margin-top: 8px">
      <div class="form-group col-sm-12 col-lg-12 text-left">
        <div class="d-flex align-items-center" style="gap: 12px; margin-bottom: 8px">
          <input
            type="checkbox"
            :id="'include_stock_movements'"
            v-model="includeStockMovements"
            style="width: 16px; height: 16px; cursor: pointer"
          />
          <label :for="'include_stock_movements'" class="control-label mb-0" style="cursor: pointer">
            {{ $t("scale.quantity_warnings.include_stock_movements") }}
          </label>
        </div>
        <div v-if="includeStockMovements" style="padding-left: 28px" class="col-sm-12 col-lg-4">
          <DxTagBox
            v-model="localData.stock_movement_type_ids"
            :data-source="transactionTypesList"
            display-expr="name"
            value-expr="uuid"
            :show-selection-controls="true"
            :search-enabled="true"
            :placeholder="$t('select_all')"
            :show-clear-button="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import DxTagBox from "devextreme-vue/tag-box";
import DxNumberBox from "devextreme-vue/number-box";
import { required } from "vuelidate/src/validators";

export default {
  name: "QuantityWarningSettings",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.formData },
      transactionTypesList: [],
    };
  },
  computed: {
    ...mapGetters({
      departments: "company/departments",
      weighingOnScaleTypes: "scale/weighingOnScaleTypes",
    }),
    legalEntityList() {
      return (this.departments || [])
        .filter((item) => item.is_legal_entity === 1 && item.legal_entity)
        .map((item) => ({
          uuid: item.legal_entity.uuid,
          name: item.legal_entity.name,
        }));
    },
    weighingTypesList() {
      const types = this.weighingOnScaleTypes;
      if (Array.isArray(types)) {
        return types.map((t) => ({
          weighing_type_id: t.id,
          weighing_type: t.name,
        }));
      }
      if (types && typeof types === "object") {
        return Object.keys(types).map((key) => ({
          weighing_type_id: types[key].id,
          weighing_type: types[key].name,
        }));
      }
      return [];
    },
    includeWeighing: {
      get() {
        return !!this.localData.include_weighing_on_scale;
      },
      set(val) {
        this.localData = { ...this.localData, include_weighing_on_scale: val ? 1 : 0 };
        if (!val) {
          this.localData = { ...this.localData, weighing_type_ids: [] };
        }
      },
    },
    includeStockMovements: {
      get() {
        return !!this.localData.include_stock_movements;
      },
      set(val) {
        this.localData = { ...this.localData, include_stock_movements: val ? 1 : 0 };
        if (!val) {
          this.localData = { ...this.localData, stock_movement_type_ids: [] };
        }
      },
    },
  },
  components: {
    DxSelectBox,
    DxTagBox,
    DxNumberBox,
  },
  validations: {
    localData: {
      legal_entity_uuid: { required },
      year: { required },
    },
  },
  methods: {
    ...mapActions({
      getWeighingOnScaleTypes: "scale/getWeighingOnScaleTypes",
      getQuantityWarningTransactionTypes: "scaleQuantityWarnings/getQuantityWarningTransactionTypes",
    }),
    onLegalEntityChanged(e) {
      if (e.value) {
        const entity = this.legalEntityList.find((le) => le.uuid === e.value);
        this.localData = { ...this.localData, legal_entity_name: entity?.name || "" };
      } else {
        this.localData = { ...this.localData, legal_entity_name: "" };
      }
    },
    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return [this.$t("fill_out_error")];
      }
      if (!this.localData.include_weighing_on_scale && !this.localData.include_stock_movements) {
        return [this.$t("scale.quantity_warnings.error_select_movement_type")];
      }
      const hasWeighingType = this.localData.include_weighing_on_scale && Array.isArray(this.localData.weighing_type_ids) && this.localData.weighing_type_ids.length > 0;
      const hasTransactionType = this.localData.include_stock_movements && Array.isArray(this.localData.stock_movement_type_ids) && this.localData.stock_movement_type_ids.length > 0;
      if (!hasWeighingType && !hasTransactionType) {
        return [this.$t("scale.quantity_warnings.error_select_at_least_one_type")];
      }
      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        this.localData = { ...newData };
      }
    },
  },
  watch: {
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("input", newData);
      },
    },
  },
  mounted() {
    this.getWeighingOnScaleTypes();
    this.getQuantityWarningTransactionTypes().then((response) => {
      const data = response?.data?.data || response?.data || [];
      this.transactionTypesList = Array.isArray(data) ? data : [];
    });
  },
};
</script>

<style scoped>
.section-title {
  font-weight: 600;
  font-size: 15px;
  color: #333;
  margin-bottom: 4px;
}
</style>
